/**
 * Les nombres entiers en lettres, orthographe rectifiée de 1990 : des traits d'union partout
 * (« soixante-seize-mille-quatre »), comme les items nationaux de 2026. Auto-vérifié : le texte
 * produit est relu en nombre (relireEnNombre) et le générateur lève une erreur si l'aller-retour
 * diverge (FORMATS_TANOED § 0.2).
 */

const UNITES = ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix',
    'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
const DIZAINES = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];

/** De 0 à 99. `final` : le nombre termine l'écriture (« quatre-vingts » prend alors son s). */
const deuxChiffres = (n, final) => {
    if (n < 20) return UNITES[n];
    const d = Math.floor(n / 10);
    const u = n % 10;
    if (d === 7 || d === 9) {
        // soixante-dix… quatre-vingt-dix… : la dizaine précédente + 10 à 19
        const reste = n - (d === 7 ? 60 : 80);
        return `${DIZAINES[d]}${reste === 11 && d === 7 ? '-et-' : '-'}${UNITES[reste]}`;
    }
    if (u === 0) return d === 8 && final ? 'quatre-vingts' : DIZAINES[d];
    if (u === 1 && d !== 8) return `${DIZAINES[d]}-et-un`;
    return `${DIZAINES[d]}-${UNITES[u]}`;
};

/** De 0 à 999. */
const troisChiffres = (n, final) => {
    const c = Math.floor(n / 100);
    const r = n % 100;
    if (c === 0) return deuxChiffres(r, final);
    const cent = c === 1 ? 'cent' : `${UNITES[c]}-cent${r === 0 && final ? 's' : ''}`;
    return r === 0 ? cent : `${cent}-${deuxChiffres(r, final)}`;
};

/**
 * Un entier de 0 à 999 999 999 en lettres. « mille » est invariable, « million » prend un s.
 * @param {number} n
 * @returns {string}
 */
export const enLettres = (n) => {
    if (!Number.isInteger(n) || n < 0 || n > 999999999) throw new Error(`enLettres : ${n} hors de 0 à 999 999 999`);
    if (n === 0) return 'zéro';
    const millions = Math.floor(n / 1000000);
    const milliers = Math.floor((n % 1000000) / 1000);
    const reste = n % 1000;
    const parts = [];
    // « million » est un nom : « deux-cents-millions », « quatre-vingts-millions » gardent leur s
    if (millions) parts.push(`${troisChiffres(millions, true)}-million${millions > 1 ? 's' : ''}`);
    if (milliers) parts.push(milliers === 1 ? 'mille' : `${troisChiffres(milliers, false)}-mille`);
    if (reste) parts.push(troisChiffres(reste, true));
    const texte = parts.join('-');
    if (relireEnNombre(texte) !== n) throw new Error(`enLettres : « ${texte} » ne se relit pas ${n}`);
    return texte;
};

const VALEURS = {
    zéro: 0, un: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6, sept: 7, huit: 8, neuf: 9, dix: 10,
    onze: 11, douze: 12, treize: 13, quatorze: 14, quinze: 15, seize: 16, vingt: 20, vingts: 20, trente: 30,
    quarante: 40, cinquante: 50, soixante: 60,
};

/** Relit un nombre écrit par enLettres (le second chemin de l'auto-vérification). */
export const relireEnNombre = (texte) => {
    let total = 0;
    let courant = 0;
    const mots = texte.split('-').filter((m) => m !== 'et');
    for (let i = 0; i < mots.length; i += 1) {
        const mot = mots[i];
        if (mot === 'cent' || mot === 'cents') courant = (courant || 1) * 100;
        else if (mot === 'mille') { total += (courant || 1) * 1000; courant = 0; }
        else if (mot === 'million' || mot === 'millions') { total += courant * 1000000; courant = 0; }
        else if (mot === 'quatre' && (mots[i + 1] === 'vingt' || mots[i + 1] === 'vingts')) { courant += 80; i += 1; }
        else if (mot in VALEURS) courant += VALEURS[mot];
        else throw new Error(`relireEnNombre : mot inconnu « ${mot} »`);
    }
    return total + courant;
};
